/**
 * useEmojiUsage.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Persists and retrieves per-emoji selection frequency to/from localStorage.
 * Separated from UI so it can be tested independently.
 */

import { useState } from 'react';
import type { EmojiPackId } from './types';
import { normalizeCodepoints } from './placeholder';

const STORAGE_KEY = 'emoji-usage-v2';
const MAX_RECENT = 40;

export type UsageEntry = { count: number; lastUsed: number };
export type UsageStore = Record<string, UsageEntry>;
export type EmojiItem = { packId: EmojiPackId; codepoints: string };

// ── Storage helpers ──────────────────────────────────────────────────────────

function readStore(): UsageStore {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as UsageStore;
  } catch {
    /* ignore parse errors */
  }
  return {};
}

function writeStore(store: UsageStore) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

// ── Public API ───────────────────────────────────────────────────────────────

export function buildRecentList(store: UsageStore): EmojiItem[] {
  return Object.entries(store)
    .sort(([, a], [, b]) => b.lastUsed - a.lastUsed)
    .slice(0, MAX_RECENT)
    .map(([codepoints]) => ({ packId: 'fluentui' as EmojiPackId, codepoints: normalizeCodepoints(codepoints) }));
}

export function useEmojiUsage() {
  const [store, setStore] = useState<UsageStore>(readStore);

  function recordUsage(codepoints: string) {
    const key = normalizeCodepoints(codepoints);
    setStore((prev) => {
      const next = { ...prev };
      const entry = next[key] ?? { count: 0, lastUsed: 0 };
      next[key] = { count: entry.count + 1, lastUsed: Date.now() };
      writeStore(next);
      return next;
    });
  }

  return { store, recordUsage, recentItems: buildRecentList(store) };
}
